import {
    categoryConditionTargets,
    hasInvalidCategorySpendLimitTargets,
    isLiveImputationTarget,
} from './expense-rule-imputation-targets';

const EXPENSE_SCOPE = 'expense';
const COMPARISON_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq'];

function isSet(value) {
    return value !== '' && value !== null && value !== undefined;
}

export function getConditionValidationErrorKey(condition, scope, liveIds) {
    if (!condition?.type) {
        return 'expense_rules.condition_incomplete';
    }

    const params = condition.parameters || {};

    if (scope === EXPENSE_SCOPE && condition.type === 'category_spend_limit') {
        const targets = categoryConditionTargets(params);
        if (targets.length === 0) {
            return 'expense_rules.category_required';
        }
        if (!targets.every(target => isLiveImputationTarget(target, liveIds))) {
            return 'expense_rules.invalid_imputation_targets';
        }
        if (!isSet(params.limit)) {
            return 'expense_rules.limit_amount_required';
        }
        return null;
    }

    if (scope === EXPENSE_SCOPE && condition.type === 'max_tip_percentage') {
        if (!isSet(params.maxPercent)) {
            return 'expense_rules.tip_percentage_required';
        }
        return null;
    }

    if (scope === EXPENSE_SCOPE && condition.type === 'travel_expense_limit') {
        if (!params.location) {
            return 'expense_rules.travel_location_required';
        }
        if (!isSet(params.limit)) {
            return 'expense_rules.limit_amount_required';
        }
        return null;
    }

    if (scope === 'expense_account' && condition.type === 'monthly_submitted_report_count') {
        if (!COMPARISON_OPERATORS.includes(params.operator)) {
            return 'expense_rules.operator_required';
        }
        if (!isSet(params.value)) {
            return 'expense_rules.report_count_required';
        }
        return null;
    }

    if (scope === 'expense_account' && condition.type === 'expense_account_amount_threshold') {
        if (!COMPARISON_OPERATORS.includes(params.operator)) {
            return 'expense_rules.operator_required';
        }
        if (!isSet(params.value)) {
            return 'expense_rules.amount_threshold_required';
        }
        return null;
    }

    if (scope === 'expense_account' && condition.type === 'employee_number_required') {
        return null;
    }

    // XPEN-3026-007/T02: valid in both scopes — employee_rank is the first
    // condition type shared by expenseRuleTypes and accountRuleTypes.
    if (condition.type === 'employee_rank') {
        const rankIds = Array.isArray(params.rank_ids) ? params.rank_ids : [];
        if (rankIds.length === 0) {
            return 'expense_rules.rank_required';
        }
        return null;
    }

    return 'expense_rules.condition_incomplete';
}

export function collectExpenseRuleValidationErrorKeys(rule, liveIds) {
    if (!rule?.active) {
        return [];
    }

    const scope = rule.scope || EXPENSE_SCOPE;
    const keys = [];

    if (!rule.effect) {
        keys.push('expense_rules.effect_required');
    }

    if (!rule.message || !String(rule.message).trim()) {
        keys.push('expense_rules.message_required');
    }

    if (hasInvalidCategorySpendLimitTargets(rule.conditions, scope, liveIds)) {
        keys.push('expense_rules.invalid_imputation_targets');
    }

    const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
    if (conditions.length === 0) {
        keys.push('expense_rules.conditions_empty');
        return dedupeKeys(keys);
    }

    conditions.forEach((condition) => {
        const key = getConditionValidationErrorKey(condition, scope, liveIds);
        if (key && key !== 'expense_rules.invalid_imputation_targets') {
            keys.push(key);
        }
    });

    return dedupeKeys(keys);
}

function dedupeKeys(keys) {
    return [...new Set(keys)];
}

export function formatExpenseRuleValidationError(errorKeys, readLabel) {
    if (!errorKeys?.length) {
        return null;
    }

    return errorKeys
        .map((key) => {
            const translated = readLabel(key);
            return translated === key ? key : translated;
        })
        .join('\n');
}
